import { isProductInStock, filterBrowsableProducts } from './catalogProductShape';
import { getProductDisplayPrice } from './filterPrice';
import { isCardExpired, isCardNew, isOfferExpired } from './offerStatus';
import { multistoreOffersEnabled } from '../config/features';

export const MAX_CARD_OFFER_PREVIEW = 4;

function normalizeKeyPart(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Group key: shared SKU when present, otherwise brand + product name. */
export function getOfferGroupKey(product) {
  if (!product) return '';
  const sku = String(product.sku || '').trim();
  if (sku) return `sku:${sku.toLowerCase()}`;
  const brand = normalizeKeyPart(product.brand);
  const name = normalizeKeyPart(product.name);
  if (!brand && !name) {
    const id = product.id || product._id;
    return id ? `id:${String(id)}` : '';
  }
  return `nm:${brand}::${name}`;
}

export function getStoreLabel(product) {
  const label = String(product?.storeName || product?.vendor || '').trim();
  return label || 'Store';
}

export function isOfferInStock(product) {
  return product?.inStock !== false && isProductInStock(product);
}

export function sortProductOffers(offers = []) {
  return [...offers].sort((a, b) => {
    const aExp = isOfferExpired(a) ? 1 : 0;
    const bExp = isOfferExpired(b) ? 1 : 0;
    if (aExp !== bExp) return aExp - bExp;
    const aIn = isOfferInStock(a) ? 0 : 1;
    const bIn = isOfferInStock(b) ? 0 : 1;
    if (aIn !== bIn) return aIn - bIn;
    const pa = getProductDisplayPrice(a) ?? 0;
    const pb = getProductDisplayPrice(b) ?? 0;
    if (pa !== pb) return pa - pb;
    return getStoreLabel(a).localeCompare(getStoreLabel(b));
  });
}

export function computeFromPrice(offers = []) {
  const sorted = sortProductOffers(offers);
  const active = sorted.filter((o) => !isOfferExpired(o));
  const poolBase = active.length ? active : sorted;
  const inStock = poolBase.filter(isOfferInStock);
  const pool = inStock.length ? inStock : poolBase;
  if (!pool.length) return 0;
  return Math.min(...pool.map((p) => getProductDisplayPrice(p) ?? Infinity));
}

export function pickDefaultOffer(offers = [], preferred = null) {
  const sorted = sortProductOffers(offers);
  if (!sorted.length) return null;
  if (preferred) {
    const want = String(preferred.id || preferred._id);
    const match = sorted.find((o) => String(o.id || o._id) === want);
    if (match) return match;
  }
  return sorted.find(isOfferInStock) || sorted[0];
}

export function getOffersInGroup(product, allProducts = []) {
  if (!product) return [];
  const key = getOfferGroupKey(product);
  const matches = (allProducts || []).filter((p) => getOfferGroupKey(p) === key);
  return sortProductOffers(matches.length ? matches : [product]);
}

export function buildOfferGroup(offers = []) {
  const sorted = sortProductOffers(offers);
  const representative = pickDefaultOffer(sorted) || sorted[0];
  if (!multistoreOffersEnabled) {
    return { ...representative, offerGroup: undefined };
  }
  const fromPrice = computeFromPrice(sorted);
  const cheapestOffer =
    sorted.find((p) => isOfferInStock(p) && (getProductDisplayPrice(p) ?? 0) === fromPrice) ||
    pickDefaultOffer(sorted);
  const offerGroup = {
    key: getOfferGroupKey(representative),
    offers: sorted,
    storeCount: sorted.length,
    fromPrice,
    cheapestStoreName: getStoreLabel(cheapestOffer),
    maxDiscount: Math.max(0, ...sorted.map((p) => Number(p.discount) || 0))
  };
  return { ...representative, offerGroup };
}

export function groupProductsForGrid(products = []) {
  if (!multistoreOffersEnabled) {
    return (products || []).map((p) => ({ ...p, offerGroup: undefined }));
  }
  const map = new Map();
  (products || []).forEach((p) => {
    const key = getOfferGroupKey(p);
    if (!key || key.startsWith('id:')) {
      map.set(`solo:${p.id || p._id}`, [p]);
      return;
    }
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  });
  return Array.from(map.values()).map((list) => buildOfferGroup(list));
}

/**
 * Include every store listing for groups already visible in `filteredProducts`
 * (from the full catalog). Keeps Option B accurate when sidebar store/subcategory
 * filters would otherwise leave only one row per SKU.
 */
export function expandWithOfferSiblings(filteredProducts = [], allProducts = []) {
  const browsableFiltered = filterBrowsableProducts(filteredProducts);
  if (!multistoreOffersEnabled) return browsableFiltered;
  if (!browsableFiltered.length) return [];
  const browsableCatalog = filterBrowsableProducts(allProducts);
  if (!browsableCatalog.length) return browsableFiltered;

  const siblingsByKey = new Map();
  browsableCatalog.forEach((p) => {
    const key = getOfferGroupKey(p);
    if (!key || key.startsWith('id:')) return;
    if (!siblingsByKey.has(key)) siblingsByKey.set(key, []);
    siblingsByKey.get(key).push(p);
  });

  const out = new Map();
  browsableFiltered.forEach((p) => {
    const key = getOfferGroupKey(p);
    const pool =
      key && !key.startsWith('id:') && siblingsByKey.has(key)
        ? siblingsByKey.get(key)
        : [p];
    pool.forEach((item) => {
      const id = item?.id ?? item?._id;
      if (id != null) out.set(String(id), item);
    });
  });
  return [...out.values()];
}

/** Filtered browse list + full catalog → grouped cards (Option B). */
export function groupBrowsableProductsForGrid(filteredProducts = [], allProducts = []) {
  const browsableFiltered = filterBrowsableProducts(filteredProducts);
  const browsableCatalog = filterBrowsableProducts(allProducts?.length ? allProducts : browsableFiltered);
  const expanded = expandWithOfferSiblings(browsableFiltered, browsableCatalog);
  return groupProductsForGrid(expanded);
}

/** Browsable list → one card per offer group (Option B). */
export function groupBrowsableProducts(productList = [], { filterFn, allProducts } = {}) {
  const list = typeof filterFn === 'function' ? filterFn(productList) : productList || [];
  const catalog = allProducts?.length ? allProducts : list;
  return groupBrowsableProductsForGrid(list, catalog);
}

function parseRecencyMs(value) {
  if (!value) return 0;
  const t = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(t) ? t : 0;
}

/** Same date the New badge uses: offer start, else createdAt. Future starts do not count as newest. */
function getListingNewnessMs(offer) {
  const start = parseRecencyMs(offer?.offerStartsAt);
  const created = parseRecencyMs(offer?.createdAt);
  const startAt = start || created;
  if (!startAt) return 0;
  if (startAt > Date.now()) return created;
  return startAt;
}

/** Newest listing time for a grouped card, matching New-badge date rules. */
export function getGroupedProductRecency(product) {
  const offers = product?.offerGroup?.offers?.length
    ? product.offerGroup.offers
    : [product];
  const active = offers.filter((offer) => !isOfferExpired(offer));
  const pool = active.length ? active : offers;
  let newest = 0;
  pool.forEach((offer) => {
    newest = Math.max(newest, getListingNewnessMs(offer));
  });
  return newest;
}

/** Price used for Low/High sort — works with or without multistore grouping. */
export function getGroupedSortPrice(product) {
  const fromGroup = product?.offerGroup?.fromPrice;
  if (Number.isFinite(fromGroup) && fromGroup > 0) return fromGroup;
  return getProductDisplayPrice(product) ?? Number.POSITIVE_INFINITY;
}

/** Discount % used for Highest Discount sort. */
export function getGroupedSortDiscount(product) {
  const fromGroup = product?.offerGroup?.maxDiscount;
  if (Number.isFinite(fromGroup) && fromGroup > 0) return fromGroup;
  const d = Number(product?.discount);
  return Number.isFinite(d) && d > 0 ? d : 0;
}

export function sortGroupedProducts(groupedProducts = [], sortBy = 'popular') {
  const list = [...groupedProducts];
  switch (sortBy) {
    case 'price-low':
      list.sort((a, b) => getGroupedSortPrice(a) - getGroupedSortPrice(b));
      break;
    case 'price-high':
      list.sort((a, b) => getGroupedSortPrice(b) - getGroupedSortPrice(a));
      break;
    case 'discount':
      list.sort((a, b) => getGroupedSortDiscount(b) - getGroupedSortDiscount(a));
      break;
    case 'newest':
      list.sort((a, b) => {
        const aNew = isCardNew(a) ? 1 : 0;
        const bNew = isCardNew(b) ? 1 : 0;
        if (aNew !== bNew) return bNew - aNew;
        return getGroupedProductRecency(b) - getGroupedProductRecency(a);
      });
      break;
    default:
      list.sort((a, b) => {
        const af = a.isFeatured ? 1 : 0;
        const bf = b.isFeatured ? 1 : 0;
        if (af !== bf) return bf - af;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
  }
  return partitionExpiredLast(list);
}

/** Live deals first, fully expired cards last — order within each group is preserved. */
export function partitionExpiredLast(groupedProducts = []) {
  const live = [];
  const expired = [];
  groupedProducts.forEach((product) => {
    if (isCardExpired(product)) expired.push(product);
    else live.push(product);
  });
  return live.concat(expired);
}

export function formatOfferPrice(amount) {
  return `Ksh ${Number(amount || 0).toLocaleString('en-KE', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  })}`;
}
